import { useState, useRef, useLayoutEffect } from 'react';
import { format } from 'date-fns';
import { useTracking } from 'react-tracking';
import {
  Grid,
  Box,
  Text,
  Button,
  useTheme,
  usePreventWindowScroll,
} from '@simplywallst/ui-core';
import BellRingingSolid from '@simplywallst/ui-core/icons/BellRingingSolid';
import { useHtmlReactParser } from '@/pages/Narrative/hooks';
import If from '@/components/If';
import ActionSheet, {
  Header,
  Panel,
  Sheet,
  Background,
} from '@/components/ActionSheet';
import { Narrative } from '@/pages/Narrative/hooks/useNarrative';
import { AnnouncementActions } from '@/pages/Narrative/components/AnnouncementActions';

import { TopContainer, TruncatedAnnouncement } from './styled';

export interface Props {
  slug: Narrative['slug'];
  announcement: NonNullable<Narrative['announcements']>[number];
}

const PreventWindowScroll = () => {
  usePreventWindowScroll();
  return null;
};

export const Announcement = ({ slug, announcement }: Props) => {
  const { trackEvent } = useTracking();
  const contentRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(true);
  const content = useHtmlReactParser({ html: announcement.contentHtml }) as [];
  const createdDate = format(new Date(announcement.createdAt), 'dd MMMM, yyyy');

  useLayoutEffect(() => {
    if (contentRef.current?.clientHeight) {
      setIsTruncated(
        contentRef.current.clientHeight < contentRef.current.scrollHeight
      );
    }
  }, []);

  return (
    <Box
      backgroundColor="noir"
      borderRadiusLg="x1spacePx"
      marginTopMd="x0"
      marginTop="x1spacePx"
    >
      <TopContainer
        templateColumns="min-content 1fr min-content"
        justifyContent="space-between"
        borderTopLeftRadiusLg="x1"
        borderTopRightRadiusLg="x1"
        paddingX="x3"
        paddingY="x2"
        gap={`${theme.x2spacePx}px`}
      >
        <BellRingingSolid fill={theme.color.chart06} />
        <Text typography="small" color="lightGray">
          Announcement on {createdDate}
        </Text>
        <AnnouncementActions slug={slug} announcement={announcement} />
      </TopContainer>
      <Grid
        borderRadiusLg="x1"
        backgroundColor="noir"
        padding="x2"
        gap={`${theme.x2spacePx}px`}
      >
        <TruncatedAnnouncement
          data-cy-id="narrative-announcement-content"
          ref={contentRef}
          className="[word-break:break-word]"
        >
          {content}
        </TruncatedAnnouncement>
        {isTruncated && (
          <Button
            styleType="loudLarge"
            data-cy-id="narrative-announcement-see-more"
            onClick={() => {
              trackEvent({
                action: 'view',
                modifier: 'announcement',
              });
              setIsModalOpen(true);
            }}
          >
            See more
          </Button>
        )}
      </Grid>
      <If match={isModalOpen}>
        {() => (
          <ActionSheet open={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <PreventWindowScroll />
            <Background onClick={() => setIsModalOpen(false)} />
            <Sheet>
              <Grid
                data-cy-id="narrative-announcement-modal"
                templateRows={`calc(40px + ${theme.safeAreaInsetTop}) auto`}
              >
                <Panel templateRows="max-content 1fr" row="2">
                  <Header>
                    <Text
                      typography="baseStrong"
                      color="white"
                      data-cy-id="narrative-announcement-modal-heading"
                    >
                      {createdDate}
                    </Text>
                  </Header>
                  <div
                    className="scrollbar grid max-h-[calc(100dvh-154px)] auto-rows-max overflow-y-auto p-x3 [word-break:break-word]"
                    data-cy-id="narrative-announcement-modal-content"
                  >
                    {content}
                  </div>
                  <div className="grid grid-cols-[max-content] justify-end border-0 border-t border-solid border-t-p-grey-10 p-x1">
                    <Button
                      styleType="loudLarge"
                      onClick={() => setIsModalOpen(false)}
                      data-cy-id="narrative-announcement-modal-close-button"
                    >
                      Close
                    </Button>
                  </div>
                </Panel>
              </Grid>
            </Sheet>
          </ActionSheet>
        )}
      </If>
    </Box>
  );
};
